/**
 * ToolActivityIndicator - Phase 8: UX Review (Story 8.1)
 * BMad Agent: Sally (UX), UI Designer
 * 
 * Shows real-time tool usage feedback to users:
 * - "Searching the web for current data..."
 * - "Verifying claims with external sources..."
 * - "Consulting knowledge graph..."
 * 
 * CRITICAL: User-friendly language (no technical jargon)
 */

import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, Database, Globe, Zap, BookOpen, Brain, Target, Languages, FileText, TrendingUp, Shield } from 'lucide-react';

// Tool activity types mapped to user-friendly descriptions
export type ToolActivityType = 
  | 'web_search'
  | 'verification'
  | 'knowledge_graph'
  | 'rag_retrieval'
  | 'reranking'
  | 'translation'
  | 'document_generation'
  | 'trend_analysis'
  | 'competitive_intel'
  | 'quality_check'
  | 'learning'
  | 'ml_classification';

export interface ToolActivity {
  id: string;
  type: ToolActivityType;
  startTime: Date;
  endTime?: Date;
  status: 'active' | 'complete' | 'error';
  details?: string;
  sourcesChecked?: number;
}

interface ToolActivityIndicatorProps {
  activities: ToolActivity[];
  className?: string;
  compact?: boolean;
  showCompleted?: boolean;
}

// Sally's UX-optimized tool descriptions (no technical jargon!)
const TOOL_DESCRIPTIONS: Record<ToolActivityType, { label: string; icon: React.ComponentType<any>; activeText: string; completeText: string }> = {
  web_search: {
    label: 'Web Research',
    icon: Globe,
    activeText: 'Searching the web for current information...',
    completeText: 'Found relevant web sources'
  },
  verification: {
    label: 'Fact Checking',
    icon: Shield,
    activeText: 'Verifying claims with external sources...',
    completeText: 'Claims verified'
  },
  knowledge_graph: {
    label: 'Knowledge Lookup',
    icon: Database,
    activeText: 'Consulting our knowledge base...',
    completeText: 'Knowledge retrieved'
  },
  rag_retrieval: {
    label: 'Context Retrieval',
    icon: BookOpen,
    activeText: 'Finding relevant context...',
    completeText: 'Context gathered'
  },
  reranking: {
    label: 'Quality Optimization',
    icon: Target,
    activeText: 'Selecting the best information...',
    completeText: 'Best results selected'
  },
  translation: {
    label: 'Translation',
    icon: Languages,
    activeText: 'Translating content...',
    completeText: 'Translation complete'
  },
  document_generation: {
    label: 'Document Creation',
    icon: FileText,
    activeText: 'Creating your document...',
    completeText: 'Document ready'
  },
  trend_analysis: {
    label: 'Trend Analysis',
    icon: TrendingUp,
    activeText: 'Analyzing market trends...',
    completeText: 'Trends identified'
  },
  competitive_intel: {
    label: 'Competitive Research',
    icon: Search,
    activeText: 'Researching competitors...',
    completeText: 'Competitive insights gathered'
  },
  quality_check: {
    label: 'Quality Review',
    icon: CheckCircle,
    activeText: 'Reviewing quality...',
    completeText: 'Quality verified'
  },
  learning: {
    label: 'Learning',
    icon: Brain,
    activeText: 'Learning from this interaction...',
    completeText: 'Insight captured'
  },
  ml_classification: {
    label: 'Understanding Request',
    icon: Zap,
    activeText: 'Understanding your request...',
    completeText: 'Request understood'
  }
};

/**
 * Individual tool activity item
 */
function ToolActivityItem({ activity, compact }: { activity: ToolActivity; compact?: boolean }) {
  const config = TOOL_DESCRIPTIONS[activity.type];
  const Icon = config.icon;
  const isActive = activity.status === 'active';
  const isError = activity.status === 'error';
  
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!isActive) return;
    
    const timer = setInterval(() => {
      setElapsed(Date.now() - activity.startTime.getTime());
    }, 100);
    
    return () => clearInterval(timer);
  }, [isActive, activity.startTime]);
  
  const formatElapsed = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    return seconds < 1 ? '<1s' : `${seconds}s`;
  };

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
        isActive 
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
          : isError
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      }`}>
        <Icon className={`w-3 h-3 ${isActive ? 'animate-pulse' : ''}`} />
        <span>{config.label}</span>
        {isActive && <span className="text-[10px] opacity-75">{formatElapsed(elapsed)}</span>}
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
      isActive 
        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' 
        : isError
          ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
    }`}>
      <div className={`p-2 rounded-full ${
        isActive 
          ? 'bg-blue-100 dark:bg-blue-800' 
          : isError
            ? 'bg-red-100 dark:bg-red-800'
            : 'bg-green-100 dark:bg-green-800'
      }`}>
        <Icon className={`w-4 h-4 ${
          isActive 
            ? 'text-blue-600 dark:text-blue-300 animate-pulse' 
            : isError
              ? 'text-red-600 dark:text-red-300'
              : 'text-green-600 dark:text-green-300'
        }`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-medium ${
            isActive 
              ? 'text-blue-700 dark:text-blue-300' 
              : isError
                ? 'text-red-700 dark:text-red-300'
                : 'text-green-700 dark:text-green-300'
          }`}>
            {config.label}
          </span>
          {isActive && (
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {formatElapsed(elapsed)}
            </span>
          )}
        </div>
        
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
          {isActive ? config.activeText : isError ? 'An error occurred' : config.completeText}
        </p>
        
        {activity.sourcesChecked && activity.sourcesChecked > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {activity.sourcesChecked} sources checked
          </p>
        )}
        
        {activity.details && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 truncate">
            {activity.details}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Main ToolActivityIndicator component
 * Shows all active and recently completed tool activities
 */
export function ToolActivityIndicator({ 
  activities, 
  className = '',
  compact = false,
  showCompleted = true
}: ToolActivityIndicatorProps) {
  const activeActivities = activities.filter(a => a.status === 'active');
  const completedActivities = activities.filter(a => a.status !== 'active');
  
  // In compact mode, show as inline badges
  if (compact) {
    return (
      <div className={`flex flex-wrap gap-1.5 ${className}`}>
        {activeActivities.map(activity => (
          <ToolActivityItem key={activity.id} activity={activity} compact />
        ))}
        {showCompleted && completedActivities.slice(0, 3).map(activity => (
          <ToolActivityItem key={activity.id} activity={activity} compact />
        ))}
      </div>
    );
  }

  // Full mode with detailed cards
  return (
    <div className={`space-y-2 ${className}`}>
      {activeActivities.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Currently Working On
          </h4>
          {activeActivities.map(activity => (
            <ToolActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
      )}
      
      {showCompleted && completedActivities.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Completed
          </h4>
          {completedActivities.slice(0, 5).map(activity => (
            <ToolActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ToolActivityIndicator;
